/**
 * PrintListFilterCard — enterprise layout for Preview step:
 * Lọc & Chọn Chứng Từ In (quick search + column filters + tick list).
 *
 * Layout:
 *  1) Header: title + “đã tích N/M”
 *  2) Toolbar: [search…] [Chọn tất cả] [Bỏ chọn] [Chỉ đã tích] [Lọc cột]
 *  3) Optional advanced panel (column conditions + subtitle chips)
 *  4) Scroll list rows
 */
import * as React from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Filter,
  ListChecks,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
  AlertTriangle,
} from "lucide-react";

import type { DetailedGroup } from "@/utils/dataGrouper";
import {
  activeColumnFilterCount,
  groupColumnValue,
  preferredFilterColumn,
  type ColumnFilter,
} from "@/utils/printPageFilter";
import { safeUUID } from "@/utils/safeUUID";
import { cn } from "@/lib/utils";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface PrintListPage {
  id: string;
  label: string;
  group: DetailedGroup;
  enabled?: boolean;
}

export interface PrintListFilterCardProps {
  groupsCount: number;
  groupingColumns: string[];
  filterColumns: string[];

  pages: PrintListPage[];
  visiblePages: PrintListPage[];
  enabledCount: number;
  activePageId: string | null;

  groupSearch: string;
  onGroupSearchChange: (v: string) => void;

  columnFilters: ColumnFilter[];
  onColumnFiltersChange: (next: ColumnFilter[]) => void;
  columnFilterOpen: boolean;
  onColumnFilterOpenChange: (open: boolean) => void;

  listExtraColumns: string[];
  onListExtraColumnsChange: (cols: string[]) => void;

  printFilterMode: boolean;
  onPrintFilterModeChange: (v: boolean) => void;

  editingPageId: string;
  onEditingPageIdChange: (id: string) => void;

  onSelectPage: (id: string) => void;
  onToggleEnabled: (id: string, enabled: boolean) => void;
  onSetAllEnabled: (enabled: boolean) => void;
  onMovePage: (id: string, direction: -1 | 1) => void;
  onRenamePage: (id: string, label: string) => void;
  onGoToDataStep: () => void;
}

export function PrintListFilterCard({
  groupsCount,
  groupingColumns,
  filterColumns,
  pages,
  visiblePages,
  enabledCount,
  activePageId,
  groupSearch,
  onGroupSearchChange,
  columnFilters,
  onColumnFiltersChange,
  columnFilterOpen,
  onColumnFilterOpenChange,
  listExtraColumns,
  onListExtraColumnsChange,
  printFilterMode,
  onPrintFilterModeChange,
  editingPageId,
  onEditingPageIdChange,
  onSelectPage,
  onToggleEnabled,
  onSetAllEnabled,
  onMovePage,
  onRenamePage,
  onGoToDataStep,
}: PrintListFilterCardProps) {
  const [collapsed, setCollapsed] = React.useState(false);
  const activeCols = activeColumnFilterCount(columnFilters);
  const hasQuick = groupSearch.trim().length > 0;
  const hasAnyFilter = hasQuick || activeCols > 0;
  const filteredOut = pages.length - visiblePages.length;

  function clearAllFilters() {
    onGroupSearchChange("");
    onColumnFiltersChange([]);
  }

  function addColumnFilter() {
    if (columnFilters.length >= 6) return;
    onColumnFiltersChange([
      ...columnFilters,
      {
        id: safeUUID(),
        column: preferredFilterColumn(filterColumns),
        value: "",
      },
    ]);
    if (!columnFilterOpen) onColumnFilterOpenChange(true);
  }

  function patchFilter(id: string, patch: Partial<ColumnFilter>) {
    onColumnFiltersChange(
      columnFilters.map((x) => (x.id === id ? { ...x, ...patch } : x))
    );
  }

  function removeFilter(id: string) {
    onColumnFiltersChange(columnFilters.filter((x) => x.id !== id));
  }

  function toggleExtraCol(h: string) {
    if (listExtraColumns.includes(h)) {
      onListExtraColumnsChange(listExtraColumns.filter((x) => x !== h));
      return;
    }
    if (listExtraColumns.length >= 3) {
      onListExtraColumnsChange([...listExtraColumns.slice(1), h]);
      return;
    }
    onListExtraColumnsChange([...listExtraColumns, h]);
  }

  return (
    <Card className="shadow-xs overflow-hidden">
      <CardHeader className="space-y-0 border-b bg-muted/20 px-3 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-1.5">
          <button
            type="button"
            className="flex items-center gap-1.5 text-left"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Mở rộng" : "Thu gọn"}
          >
            <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", !collapsed && "rotate-90")} />
            <ListChecks className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-sm font-semibold">Lọc & Chọn Chứng Từ In</span>
          </button>
          {pages.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <Badge variant="secondary" className="h-5 text-[10px] font-normal">
                Đã tích {enabledCount}/{pages.length}
              </Badge>
              {hasAnyFilter && (
                <Badge variant="outline" className="h-5 text-[10px] font-normal">
                  Hiện {visiblePages.length}
                  {filteredOut > 0 ? ` · ẩn ${filteredOut}` : ""}
                </Badge>
              )}
            </div>
          )}
        </div>
        {!collapsed && (
        <CardDescription className="pt-0.5 text-[11px] leading-snug">
          Tìm mọi trường · tích = in
          {groupsCount > 0 ? ` · ${groupsCount} chứng từ` : ""}
        </CardDescription>
        )}
      </CardHeader>

      {!collapsed && (
      <CardContent className="space-y-2 p-2.5">
        {groupingColumns.length === 0 ? (
          <Alert className="border-amber-400 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-xs text-amber-800">
              Chưa có cột nhóm để tạo danh sách
            </AlertTitle>
            <AlertDescription className="text-xs text-amber-700">
              Quay lại bước{" "}
              <button
                type="button"
                className="font-semibold text-primary underline underline-offset-2"
                onClick={onGoToDataStep}
              >
                Dữ Liệu & Nhóm
              </button>{" "}
              để chọn cột nhóm chính.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Toolbar: search full-width + compact action row */}
            <div className="space-y-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="h-8 pl-8 pr-8 text-xs"
                  placeholder={
                    filterColumns.length > 0
                      ? `Tìm mã, tên, PO… (${filterColumns.length} cột)`
                      : "Tìm chứng từ…"
                  }
                  value={groupSearch}
                  onChange={(e) => onGroupSearchChange(e.target.value)}
                  aria-label="Tìm chứng từ theo mọi trường"
                />
                {hasQuick && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Xóa tìm kiếm"
                    onClick={() => onGroupSearchChange("")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => onSetAllEnabled(true)}
                >
                  Tất cả
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => onSetAllEnabled(false)}
                >
                  Bỏ
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={printFilterMode ? "default" : "outline"}
                  className="h-7 gap-1 px-2 text-[11px]"
                  title="Chỉ hiển thị các mục đã tích để in"
                  onClick={() => onPrintFilterModeChange(!printFilterMode)}
                >
                  <Filter className="h-3 w-3" />
                  Đã tích
                </Button>

                <div className="mx-0.5 hidden h-4 w-px bg-border sm:block" />

                <Button
                  type="button"
                  size="sm"
                  variant={columnFilterOpen || activeCols > 0 ? "default" : "outline"}
                  className="h-7 gap-1 px-2 text-[11px]"
                  onClick={() => onColumnFilterOpenChange(!columnFilterOpen)}
                >
                  Cột
                  {activeCols > 0 && (
                    <Badge
                      variant="secondary"
                      className="h-4 min-w-4 justify-center px-1 text-[10px]"
                    >
                      {activeCols}
                    </Badge>
                  )}
                </Button>

                {hasAnyFilter && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-1.5 text-[11px] text-muted-foreground"
                    onClick={clearAllFilters}
                  >
                    Xóa
                  </Button>
                )}
              </div>
            </div>

            {/* Advanced column filters */}
            {columnFilterOpen && (
              <div className="space-y-2 rounded-md border bg-muted/15 p-2">
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Điều kiện cột <strong className="font-medium text-foreground">VÀ</strong>{" "}
                  · ô tìm phía trên vẫn quét mọi trường
                </p>

                {columnFilters.length === 0 ? (
                  <p className="text-[11px] italic text-muted-foreground">
                    Chưa có điều kiện.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {columnFilters.map((f) => (
                      <div
                        key={f.id}
                        className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-1"
                      >
                        <Select
                          value={f.column || "__none__"}
                          onValueChange={(v) =>
                            patchFilter(f.id, {
                              column: v === "__none__" ? "" : v,
                            })
                          }
                        >
                          <SelectTrigger className="h-7 w-full text-[11px]">
                            <SelectValue placeholder="Cột" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            <SelectItem value="__none__">— Cột —</SelectItem>
                            {filterColumns.map((h) => (
                              <SelectItem key={h} value={h} className="text-xs">
                                {h}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          className="h-7 text-xs"
                          placeholder="Chứa…"
                          value={f.value}
                          onChange={(e) => patchFilter(f.id, { value: e.target.value })}
                        />
                        <button
                          type="button"
                          className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                          title="Xóa điều kiện"
                          onClick={() => removeFilter(f.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 px-2 text-[11px]"
                    disabled={columnFilters.length >= 6 || filterColumns.length === 0}
                    onClick={addColumnFilter}
                  >
                    <Plus className="h-3 w-3" /> Thêm cột
                  </Button>
                </div>

                {filterColumns.length > 0 && (
                  <div className="space-y-1 border-t border-border/60 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Hiện thêm dưới mã (tối đa 3)
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {filterColumns.slice(0, 14).map((h) => {
                        const on = listExtraColumns.includes(h);
                        return (
                          <button
                            key={h}
                            type="button"
                            className={cn(
                              "rounded-md border px-1.5 py-0.5 text-[10px] transition-colors",
                              on
                                ? "border-primary/40 bg-primary/10 font-medium text-primary"
                                : "border-border/80 bg-background text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground"
                            )}
                            title={on ? `Ẩn ${h}` : `Hiện ${h}`}
                            onClick={() => toggleExtraCol(h)}
                          >
                            {h.length > 20 ? `${h.slice(0, 18)}…` : h}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── List ── */}
            <div className="max-h-80 overflow-auto rounded-lg border bg-background">
              {visiblePages.length === 0 ? (
                <p className="p-4 text-center text-xs text-muted-foreground">
                  {printFilterMode
                    ? "Không có mục đã tích. Bỏ «Chỉ đã tích» hoặc tích thêm mục."
                    : hasAnyFilter
                      ? "Không khớp bộ lọc. Bớt điều kiện hoặc Xóa lọc."
                      : "Không có chứng từ trong danh sách."}
                </p>
              ) : (
                visiblePages.map((p) => {
                  const fullIdx = pages.findIndex((pp) => pp.id === p.id);
                  const extras = listExtraColumns
                    .map((col) => {
                      const v = groupColumnValue(p.group, col);
                      return v ? { col, v } : null;
                    })
                    .filter((x): x is { col: string; v: string } => Boolean(x));
                  const isActive = activePageId === p.id;
                  const disabled = p.enabled === false;

                  return (
                    <div
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      className={cn(
                        "group flex items-start gap-2 border-b px-2.5 py-2 text-xs last:border-b-0",
                        "cursor-pointer transition-colors hover:bg-muted/60",
                        isActive && "bg-primary/5 ring-1 ring-inset ring-primary/25",
                        disabled && "opacity-45"
                      )}
                      onClick={() => onSelectPage(p.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectPage(p.id);
                        }
                      }}
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={p.enabled !== false}
                        onCheckedChange={(v) => onToggleEnabled(p.id, Boolean(v))}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Badge
                        variant="outline"
                        className="mt-0.5 h-5 min-w-6 justify-center bg-background px-1 text-[10px] tabular-nums"
                      >
                        {fullIdx + 1}
                      </Badge>

                      {editingPageId === p.id ? (
                        <Input
                          autoFocus
                          defaultValue={p.label}
                          className="h-7 flex-1 text-xs"
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => {
                            onRenamePage(p.id, e.target.value);
                            onEditingPageIdChange("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                            if (e.key === "Escape") onEditingPageIdChange("");
                          }}
                        />
                      ) : (
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold leading-snug">{p.label}</div>
                          {extras.length > 0 && (
                            <div className="mt-0.5 truncate text-[10px] font-normal leading-snug text-muted-foreground">
                              {extras.map((ex, i) => (
                                <span key={ex.col}>
                                  {i > 0 && <span className="opacity-40"> · </span>}
                                  <span className="opacity-70">{ex.col}:</span> {ex.v}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <span className="mt-0.5 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                        {p.group.totalItems} dòng
                      </span>

                      <div
                        className={cn(
                          "mt-0.5 flex shrink-0 items-center gap-0.5",
                          "opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100",
                          isActive && "opacity-100"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                          title="Lên trên"
                          disabled={fullIdx === 0}
                          onClick={() => onMovePage(p.id, -1)}
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                          title="Xuống dưới"
                          disabled={fullIdx === pages.length - 1}
                          onClick={() => onMovePage(p.id, 1)}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Đổi tên"
                          onClick={() => onEditingPageIdChange(p.id)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </CardContent>
      )}
    </Card>
  );
}
